import React, { useState, useEffect } from "react";
import { CheckCircle, XCircle, Clock, User } from "lucide-react";
import { apiFetch } from "../../../../context/AuthContext";
import { localizeWorkflowStepName } from "../../../../utils/workflowStepNames";

const BASE_URL =
  process.env.REACT_APP_BASE_URL || process.env.REACT_APP_API_URL || "";

/**
 * ApprovalTimeline
 * Vertical timeline showing the ACTUAL, department-scoped approval workflow for
 * this overtime request. The steps come straight from the WorkflowInstance the
 * backend created for this employee (already resolved against their department /
 * employeeType), so there is no hard-coded manager → admin flow here.
 */
const ApprovalTimeline = ({ record, theme, isRTL, t }) => {
  const isDark = theme === "dark";
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Reset when switching to a different request so we never show a previous
    // request's steps while the new one loads.
    setWorkflowSteps([]);

    if (!record.workflowInstanceId) return;

    let isMounted = true;
    const fetchWorkflow = async () => {
      setLoading(true);
      try {
        // Must be an absolute API URL (apiFetch does not prepend a base), and
        // the body must be parsed with .json() — apiFetch returns the raw
        // Response. Both were missing, which is why this always fell back to a
        // generic flow.
        const response = await apiFetch(
          `${BASE_URL}/workflows/${record.workflowInstanceId}`,
        );
        if (!response.ok) throw new Error(`workflow fetch ${response.status}`);
        const data = await response.json();
        if (isMounted && Array.isArray(data?.steps)) {
          setWorkflowSteps(data.steps);
        }
      } catch (err) {
        console.error("Failed to fetch workflow instance steps:", err);
        if (isMounted) setWorkflowSteps([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchWorkflow();
    return () => {
      isMounted = false;
    };
  }, [record.workflowInstanceId]);

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return <CheckCircle size={18} color="#10b981" strokeWidth={2.5} />;
      case "rejected":
        return <XCircle size={18} color="#ef4444" strokeWidth={2.5} />;
      case "queued":
        return <Clock size={18} color="#94a3b8" strokeWidth={2.5} style={{ opacity: 0.6 }} />;
      default:
        return <Clock size={18} color="#f59e0b" strokeWidth={2.5} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved": return "#10b981";
      case "rejected": return "#ef4444";
      case "queued": return "#94a3b8";
      default: return "#f59e0b";
    }
  };

  // Always start with the "request created" node.
  const requestCreatedNode = {
    label: t.requestCreated,
    status: "approved",
    sublabel: record.date
      ? new Date(record.date).toLocaleDateString(isRTL ? "ar-EG" : "en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "-",
    icon: <User size={18} color="#6366f1" strokeWidth={2.5} />,
    lineColor: "#6366f1",
  };

  // The real, department-scoped approval steps for THIS employee. Steps whose
  // conditions weren't met are materialized as SKIPPED on the backend for audit
  // purposes — they aren't part of this employee's actual path, so hide them.
  const stepNodes = workflowSteps
    .filter((step) => {
      const s = String(step.status || "").toUpperCase();
      return s !== "SKIPPED" && !step.wasSkipped;
    })
    .map((step) => {
      const stepStatus = String(step.status || "").toLowerCase(); // approved | pending | queued | rejected
      const actor = step.completedBy ? ` (${step.completedBy})` : "";
      const comments = step.comments ? ` - "${step.comments}"` : "";

      let sublabel = "";
      if (stepStatus === "approved") {
        sublabel = `${t.approvedStatus || "Approved"}${actor}${comments}`;
      } else if (stepStatus === "rejected") {
        sublabel = `${t.rejectedStatus || "Rejected"}${actor}${comments}`;
      } else if (stepStatus === "pending") {
        sublabel = t.pendingApproval || "Pending Approval";
      } else {
        sublabel = isRTL ? "في الانتظار" : "Queued";
      }

      return {
        label: localizeWorkflowStepName(step.stepName, isRTL ? "ar" : "en"),
        status: stepStatus,
        sublabel,
        icon: getStatusIcon(stepStatus),
        lineColor: getStatusColor(stepStatus),
      };
    });

  const steps = [requestCreatedNode, ...stepNodes];

  // Nothing but the created node and still loading / no instance → tell the user
  // rather than inventing a manager → admin chain that may not be the real flow.
  const showLoadingHint = loading && stepNodes.length === 0;
  const showNoWorkflowHint =
    !loading && stepNodes.length === 0; // instance missing/empty or all steps skipped

  return (
    <div style={{ padding: "4px 0" }}>
      {steps.map((step, idx) => {
        const isLast = idx === steps.length - 1;
        const statusColor = step.lineColor;

        return (
          <div
            key={idx}
            style={{
              display: "flex",
              gap: "16px",
              flexDirection: isRTL ? "row-reverse" : "row",
              position: "relative",
              minHeight: isLast ? "auto" : "64px",
            }}
          >
            {/* Timeline dot + line */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                flexShrink: 0,
                width: "36px",
              }}
            >
              {/* Dot */}
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  background: isDark
                    ? `${statusColor}20`
                    : `${statusColor}12`,
                  border: `2px solid ${statusColor}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  zIndex: 2,
                }}
              >
                {step.icon}
              </div>
              {/* Line */}
              {!isLast && (
                <div
                  style={{
                    width: "2px",
                    flex: 1,
                    background: isDark
                      ? `linear-gradient(180deg, ${statusColor}60, ${statusColor}20)`
                      : `linear-gradient(180deg, ${statusColor}50, ${statusColor}15)`,
                    marginTop: "4px",
                    borderRadius: "1px",
                  }}
                />
              )}
            </div>

            {/* Content */}
            <div
              style={{
                flex: 1,
                paddingBottom: isLast ? 0 : "20px",
                textAlign: isRTL ? "right" : "left",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: isDark ? "#f1f5f9" : "#1e293b",
                  fontFamily: "'Cairo', sans-serif",
                  marginBottom: "2px",
                  lineHeight: 1.4,
                }}
              >
                {step.label}
              </div>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "2px 10px",
                  borderRadius: "12px",
                  background: isDark
                    ? `${statusColor}15`
                    : `${statusColor}10`,
                  marginTop: "4px",
                }}
              >
                <div
                  style={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    background: statusColor,
                  }}
                />
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 500,
                    color: statusColor,
                    fontFamily: "'Cairo', sans-serif",
                  }}
                >
                  {step.sublabel}
                </span>
              </div>
            </div>
          </div>
        );
      })}

      {(showLoadingHint || showNoWorkflowHint) && (
        <div
          style={{
            marginTop: "8px",
            paddingInlineStart: "52px",
            fontSize: "12px",
            fontFamily: "'Cairo', sans-serif",
            color: isDark ? "#94a3b8" : "#64748b",
            textAlign: isRTL ? "right" : "left",
          }}
        >
          {showLoadingHint
            ? isRTL
              ? "جارٍ تحميل مسار الموافقة..."
              : "Loading approval workflow..."
            : isRTL
              ? "لا يوجد مسار موافقة مُهيّأ لهذا الطلب."
              : "No approval workflow configured for this request."}
        </div>
      )}
    </div>
  );
};

export default ApprovalTimeline;
